import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpen, Lock, Check, ChevronRight, ChevronDown, ChevronUp,
  GraduationCap, Clock, Award, ArrowRight, AlertCircle, Sparkles
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const LearningPath = () => {
  const { languageCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [pathData, setPathData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLevels, setExpandedLevels] = useState({ A1: true });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [prerequisites, setPrerequisites] = useState(null);

  const languageInfo = {
    en: { name: 'English', flag: 'gb' },
    es: { name: 'Spanish', flag: 'es' },
    fr: { name: 'French', flag: 'fr' },
    de: { name: 'German', flag: 'de' },
    fa: { name: 'Persian', flag: 'ir' },
    ar: { name: 'Arabic', flag: 'sa' },
    ru: { name: 'Russian', flag: 'ru' },
    zh: { name: 'Chinese', flag: 'cn' },
    ja: { name: 'Japanese', flag: 'jp' },
    ko: { name: 'Korean', flag: 'kr' },
    pt: { name: 'Portuguese', flag: 'pt' },
    it: { name: 'Italian', flag: 'it' },
  };

  const levelColors = {
    A1: { bg: 'from-green-500/20 to-green-600/20', border: 'border-green-500/30', text: 'text-green-400', badge: 'bg-green-500' },
    A2: { bg: 'from-blue-500/20 to-blue-600/20', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500' },
    B1: { bg: 'from-yellow-500/20 to-yellow-600/20', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500' },
    B2: { bg: 'from-orange-500/20 to-orange-600/20', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500' },
    C1: { bg: 'from-red-500/20 to-red-600/20', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500' },
    C2: { bg: 'from-purple-500/20 to-purple-600/20', border: 'border-purple-500/30', text: 'text-purple-400', badge: 'bg-purple-500' },
  };

  useEffect(() => {
    fetchLearningPath();
  }, [languageCode]);

  const fetchLearningPath = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/grammar-library/learning-path/${languageCode}`);
      setPathData(response.data);
      
      // Auto-expand the first incomplete level
      const levels = Object.entries(response.data.path);
      for (const [level, data] of levels) {
        if (data.completed < data.total) {
          setExpandedLevels({ [level]: true });
          break;
        }
      }
    } catch (err) {
      console.error('Failed to load learning path:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleLevel = (level) => {
    setExpandedLevels(prev => ({
      ...prev,
      [level]: !prev[level]
    }));
  };

  const openTopic = async (topicId, isLocked) => {
    if (isLocked) {
      // Show prerequisites
      try {
        const response = await api.get(`/grammar-library/topic/${topicId}/prerequisites`);
        setPrerequisites(response.data);
        setSelectedTopic(topicId);
      } catch (err) {
        console.error('Failed to load prerequisites:', err);
      }
    } else {
      navigate(`/grammar-library?open=${topicId}`);
    }
  };

  const FlagImage = ({ langCode, size = 32 }) => {
    const info = languageInfo[langCode];
    const countryCode = info?.flag || 'un';
    return (
      <img 
        src={`https://flagcdn.com/w80/${countryCode}.png`}
        alt={info?.name || langCode}
        className="rounded-sm object-cover"
        style={{ width: size, height: size * 0.75 }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!pathData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <p className="text-slate-400">Failed to load learning path</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <FlagImage langCode={languageCode} size={48} />
            <div>
              <h1 className="text-3xl font-bold text-white">{pathData.language_name} Learning Path</h1>
              <p className="text-slate-400">
                Master grammar step by step
              </p>
            </div>
          </div>
          
          {/* Overall Progress */}
          <div className="mt-6 max-w-md mx-auto">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-400">Overall Progress</span>
              <span className="text-amber-400 font-semibold">
                {pathData.completed_topics} / {pathData.total_topics} topics
              </span>
            </div>
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-500"
                style={{ width: `${pathData.overall_progress}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">{pathData.overall_progress}% complete</p>
          </div>
        </div>

        {/* Learning Path Levels */}
        <div className="space-y-4">
          {Object.entries(pathData.path).map(([level, data]) => {
            if (data.total === 0) return null;
            
            const colors = levelColors[level] || levelColors.A1;
            const isExpanded = expandedLevels[level];
            
            return (
              <div 
                key={level}
                className={`bg-gradient-to-r ${colors.bg} border ${colors.border} rounded-xl overflow-hidden`}
              >
                {/* Level Header */}
                <button
                  onClick={() => toggleLevel(level)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${colors.badge} flex items-center justify-center text-white font-bold text-lg`}>
                      {level}
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-semibold">{data.name}</h3>
                      <p className="text-slate-400 text-sm">
                        {data.completed} of {data.total} topics completed
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Progress bar */}
                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors.badge} transition-all duration-300`}
                        style={{ width: `${data.progress_percent}%` }}
                      />
                    </div>
                    <span className={`text-sm ${colors.text}`}>{data.progress_percent}%</span>
                    
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>
                
                {/* Topics List */}
                {isExpanded && (
                  <div className="p-4 pt-0 space-y-2">
                    {data.topics.map((topic) => (
                      <button
                        key={topic.id}
                        onClick={() => openTopic(topic.id, topic.locked)}
                        className={`w-full p-3 rounded-lg flex items-center gap-3 transition-all ${
                          topic.locked
                            ? 'bg-slate-800/30 border border-slate-700/50 cursor-not-allowed opacity-60'
                            : topic.completed
                              ? 'bg-green-500/10 border border-green-500/30 hover:border-green-500/50'
                              : 'bg-slate-800/50 border border-slate-600 hover:border-amber-500/50'
                        }`}
                      >
                        {/* Status Icon */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          topic.locked
                            ? 'bg-slate-700 text-slate-500'
                            : topic.completed
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {topic.locked ? (
                            <Lock className="w-4 h-4" />
                          ) : topic.completed ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <BookOpen className="w-4 h-4" />
                          )}
                        </div>
                        
                        {/* Topic Info */}
                        <div className="flex-1 text-left">
                          <h4 className={`font-medium ${
                            topic.locked ? 'text-slate-500' : topic.completed ? 'text-green-400' : 'text-white'
                          }`}>
                            {topic.title}
                          </h4>
                          {topic.title_native && (
                            <p className="text-slate-500 text-sm">{topic.title_native}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {topic.study_time_minutes && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {topic.study_time_minutes}m
                              </span>
                            )}
                            {topic.has_exercises && (
                              <span className="text-xs text-purple-400 flex items-center gap-1">
                                <GraduationCap className="w-3 h-3" />
                                Exercises
                              </span>
                            )}
                            {topic.locked && topic.prerequisites?.length > 0 && (
                              <span className="text-xs text-orange-400 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {topic.prerequisites.length} prerequisite(s)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Arrow */}
                        {!topic.locked && (
                          <ChevronRight className={`w-5 h-5 ${topic.completed ? 'text-green-500' : 'text-slate-500'}`} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Back to Grammar Library */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/grammar-library')}
            className="text-amber-400 hover:text-amber-300 flex items-center justify-center gap-2 mx-auto"
          >
            <BookOpen className="w-4 h-4" />
            Browse all grammar topics
          </button>
        </div>

        {/* Prerequisites Modal */}
        {prerequisites && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setPrerequisites(null)}
          >
            <div 
              className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-orange-400" />
                <h3 className="text-lg font-semibold text-white">Prerequisites Required</h3>
              </div>
              
              <p className="text-slate-400 text-sm mb-4">
                Complete these topics first to unlock <strong className="text-white">{prerequisites.topic_title}</strong>:
              </p>
              
              <div className="space-y-2 mb-6">
                {prerequisites.prerequisites.map((prereq) => (
                  <button
                    key={prereq.id}
                    onClick={() => {
                      setPrerequisites(null);
                      navigate(`/grammar-library?open=${prereq.id}`);
                    }}
                    className={`w-full p-3 rounded-lg flex items-center justify-between ${
                      prereq.completed
                        ? 'bg-green-500/10 border border-green-500/30'
                        : 'bg-slate-800/50 border border-slate-700 hover:border-amber-500/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {prereq.completed ? (
                        <Check className="w-5 h-5 text-green-400" />
                      ) : (
                        <BookOpen className="w-5 h-5 text-amber-400" />
                      )}
                      <span className={prereq.completed ? 'text-green-400' : 'text-white'}>
                        {prereq.title}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">{prereq.cefr_level}</span>
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setPrerequisites(null)}
                className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPath;
